import { FC, useMemo } from 'react';
import { useTranslation } from '@hooks/useTranslation';

interface SendHourSelectProps {
  value: number;
  timezone: string | null | undefined;
  onChange: (value: number) => void;
  disabled?: boolean;
}

/**
 * Renders the 0-23 hour picker as 12-hour clock labels (e.g. "5:00 PM") — never a bare 24-hour
 * integer, per story-169's UX spec. `dailyReportSendHour` is already an organization-local hour
 * by definition (the cron reads it via `localHour(now, org.dailyReportTimezone)`), so no
 * timezone-specific conversion is needed for the label itself; `timezone` is accepted so a
 * future revision can append the zone's short name next to the label without a prop-shape change.
 */
export const SendHourSelect: FC<SendHourSelectProps> = ({ value, timezone, onChange, disabled }) => {
  const t = useTranslation('dailySiteReport');
  const labels = useMemo(
    () =>
      Array.from({ length: 24 }, (_, hour) =>
        new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }).format(
          new Date(Date.UTC(2020, 0, 1, hour, 0, 0)),
        ),
      ),
    [],
  );

  return (
    <select
      aria-label={timezone ? `${t.sendHourAriaLabel} (${timezone})` : t.sendHourAriaLabel}
      className="border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}>
      {labels.map((label, hour) => (
        <option key={hour} value={hour}>
          {label}
        </option>
      ))}
    </select>
  );
};

export default SendHourSelect;
